import { useEffect, useState } from 'react';
import { SalesforceSupplierSearchResult } from 'sb/api/vendor';
import { VendorAdmin } from 'sb/models/prisma/VendorAdmin';
import { LinkedVendorRecord } from '../vendorsPage.types';

type UseVendorLinkingArgs = {
  vendor?: VendorAdmin;
  getVendorBySalesforceId: (salesforceId: string) => Promise<LinkedVendorRecord | undefined>;
};

export const useVendorLinking = ({ vendor, getVendorBySalesforceId }: UseVendorLinkingArgs) => {
  const [selectedSalesforceSupplierId, setSelectedSalesforceSupplierId] = useState<string>();
  const [selectedSalesforceSupplier, setSelectedSalesforceSupplier] = useState<SalesforceSupplierSearchResult>();
  const [salesforceAccountLinkedVendorLookupLoading, setSalesforceAccountLinkedVendorLookupLoading] = useState(false);
  const [salesforceAccountLinkedVendor, setSalesforceAccountLinkedVendor] = useState<LinkedVendorRecord | null>(null);

  const fetchLinkedVendorForSalesforceAccount = async (
    salesforceAccountId: string
  ): Promise<LinkedVendorRecord | null> => {
    try {
      const linkedVendor = await getVendorBySalesforceId(salesforceAccountId);
      if (linkedVendor?.id) {
        return linkedVendor;
      }
    } catch {
      // Keep ownership lookup failures non-blocking.
    }
    return null;
  };

  const clearSelectedSalesforceContext = () => {
    setSelectedSalesforceSupplierId(undefined);
    setSelectedSalesforceSupplier(undefined);
    setSalesforceAccountLinkedVendor(null);
    setSalesforceAccountLinkedVendorLookupLoading(false);
  };

  const handleSalesforceSelection = (selection: SalesforceSupplierSearchResult) => {
    setSelectedSalesforceSupplierId(selection.Id);
    setSelectedSalesforceSupplier(selection);
  };

  useEffect(() => {
    const loadLinkedVendorForSelectedSalesforceAccount = async () => {
      if (!selectedSalesforceSupplierId) {
        setSalesforceAccountLinkedVendor(null);
        setSalesforceAccountLinkedVendorLookupLoading(false);
        return;
      }

      setSalesforceAccountLinkedVendorLookupLoading(true);
      const linkedVendor = await fetchLinkedVendorForSalesforceAccount(selectedSalesforceSupplierId);
      setSalesforceAccountLinkedVendor(linkedVendor);
      setSalesforceAccountLinkedVendorLookupLoading(false);
    };

    loadLinkedVendorForSelectedSalesforceAccount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSalesforceSupplierId]);

  const isMissingSelection = !vendor || !selectedSalesforceSupplier;
  const vendorLinkedToDifferentAccount = Boolean(
    vendor?.salesforce_id && selectedSalesforceSupplier && vendor.salesforce_id !== selectedSalesforceSupplier.Id
  );
  const salesforceAccountLinkedToDifferentVendor = Boolean(
    selectedSalesforceSupplier && salesforceAccountLinkedVendor && salesforceAccountLinkedVendor.id !== vendor?.id
  );
  const selectedRecordsMatchBySalesforceId = Boolean(
    vendor?.salesforce_id && selectedSalesforceSupplier?.Id && vendor.salesforce_id === selectedSalesforceSupplier.Id
  );
  const requiresOverride = vendorLinkedToDifferentAccount || salesforceAccountLinkedToDifferentVendor;

  return {
    selectedSalesforceSupplierId,
    selectedSalesforceSupplier,
    salesforceAccountLinkedVendorLookupLoading,
    salesforceAccountLinkedVendor,
    isMissingSelection,
    vendorLinkedToDifferentAccount,
    salesforceAccountLinkedToDifferentVendor,
    selectedRecordsMatchBySalesforceId,
    requiresOverride,
    setSelectedSalesforceSupplierId,
    setSelectedSalesforceSupplier,
    setSalesforceAccountLinkedVendorLookupLoading,
    setSalesforceAccountLinkedVendor,
    fetchLinkedVendorForSalesforceAccount,
    clearSelectedSalesforceContext,
    handleSalesforceSelection
  };
};
